import React, { useEffect, useState, useRef } from 'react';
import { motion, useReducedMotion, useInView } from 'framer-motion';
import { Shield, Cpu, HardDrive } from 'lucide-react';
import { useCopy } from './copy/copyMode';
// Helper to type out command text character by character
function TypewriterText({
  text
}: {
  text: string;
}) {
  const [chars, setChars] = useState(0);
  const reducedMotion = useReducedMotion();
  useEffect(() => {
    if (reducedMotion) {
      setChars(text.length);
      return;
    }
    setChars(0);
    const interval = setInterval(() => {
      setChars(c => {
        if (c >= text.length) {
          clearInterval(interval);
          return c;
        }
        return c + 1;
      });
    }, 30); // 30ms per character
    return () => clearInterval(interval);
  }, [text, reducedMotion]);
  return <span data-id="element-1527" data-mp="3cn6eahyecw3r">{text.slice(0, chars)}</span>;
}
// Terminal lines to animate
const TERMINAL_LINES = [{
  text: 'giga@agent-7f32:~/workspace$ ',
  type: 'prompt',
  delay: 0
}, {
  text: 'giga browser export --site billing.portal',
  type: 'command',
  delay: 400
}, {
  text: 'Restored authenticated browser session',
  type: 'output',
  delay: 2000
}, {
  text: 'Downloaded 184 invoices to ./downloads/invoices/',
  type: 'output',
  delay: 2500
}, {
  text: '',
  type: 'empty',
  delay: 2600
}, {
  text: 'giga@agent-7f32:~/workspace$ ',
  type: 'prompt',
  delay: 2900
}, {
  text: 'python reconcile.py',
  type: 'command',
  delay: 3300
}, {
  text: '✓ Reconciled totals · report.pdf ready',
  type: 'success',
  delay: 4300
}, {
  text: '',
  type: 'empty',
  delay: 4400
}, {
  text: 'giga@agent-7f32:~/workspace$ ',
  type: 'prompt',
  delay: 4700
}];
export function ComputerUse() {
  const copy = useCopy();
  const reducedMotion = useReducedMotion();
  const terminalRef = useRef<HTMLDivElement>(null);
  // Only kick off the typing sequence once the terminal scrolls into view
  const inView = useInView(terminalRef, {
    once: true,
    margin: '-120px'
  });
  const [visibleLines, setVisibleLines] = useState<number>(reducedMotion ? TERMINAL_LINES.length : 0);
  useEffect(() => {
    if (reducedMotion) {
      setVisibleLines(TERMINAL_LINES.length);
      return;
    }
    if (!inView) return;
    setVisibleLines(0);
    let timeouts: NodeJS.Timeout[] = [];
    TERMINAL_LINES.forEach((line, index) => {
      const timeout = setTimeout(() => {
        setVisibleLines(index + 1);
      }, line.delay);
      timeouts.push(timeout);
    });
    return () => timeouts.forEach(clearTimeout);
  }, [reducedMotion, inView]);
  return <>
      {/* DESKTOP LAYOUT (Old Version) */}
      <section className="hidden md:block relative w-full bg-[#FAF8F4] py-32 overflow-hidden" data-id="element-1528" data-mp="18zlsy87htttv">
        <div className="absolute inset-0 z-0 pointer-events-none mix-blend-multiply opacity-90" data-id="element-1529" data-mp="14228gqk7ckth">
          <video src="/hf_20260616_083352_929e6c52-e5b2-425a-882f-180098bd55b1.mp4" autoPlay loop muted playsInline className="w-full h-full object-cover object-[20%_center] grayscale" data-id="element-1530" data-mp="zzyunva29451" />
        </div>

        <div className="relative z-10 max-w-[1280px] mx-auto px-10 flex justify-end" data-id="element-1531" data-mp="36or0cngxl8ae">
          <div className="w-[55%] xl:w-[50%] flex flex-col gap-16 relative pt-12 pr-12" data-id="element-1532" data-mp="3bmj2sbf7ccsu">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-100px'
          }} transition={{
            duration: 0.6,
            delay: 0.1
          }} data-id="element-1533" data-mp="3fovzkabz10dh">
              <span className="block text-xs font-semibold tracking-[5px] uppercase text-neutral-400 mb-6" data-id="element-1534" data-mp="2rya2fe3i90i9">
                Under the hood
              </span>
              <h2 className="text-3xl md:text-5xl font-title font-medium tracking-tight leading-[1.1] text-[#2B211A] mb-6" data-id="element-1535" data-mp="2wwbhzp1q2hoz">
                {copy.computerUse.headline}
              </h2>
              <p className="text-xl text-neutral-500 leading-relaxed max-w-xl" data-id="element-1536" data-mp="247eeis266pga">
                Every task runs on Giga's own secure machine in the cloud — it
                writes code, crunches files, and opens a browser when work lives
                behind a login.
              </p>
            </motion.div>

            <motion.div ref={terminalRef} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-100px'
          }} transition={{
            duration: 0.6,
            delay: 0.25
          }} className="relative max-w-2xl z-10 w-full" data-id="element-1537" data-mp="aoxcey27j781">
              <div className="w-full rounded-2xl bg-[#1C1510] border border-white/10 shadow-2xl overflow-hidden flex flex-col font-mono text-sm" data-id="element-1538" data-mp="tgzw6ia5b3mi">
                {/* Window Header */}
                <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.06] bg-[#241B14]" data-id="element-1539" data-mp="13fxgf90jgazp">
                  <div className="flex items-center gap-2 w-24" data-id="element-1540" data-mp="39agmwseoblvw">
                    <div className="w-3 h-3 rounded-full bg-[#FF5F56]" data-id="element-1541" data-mp="3583sh05dnaef" />
                    <div className="w-3 h-3 rounded-full bg-[#FFBD2E]" data-id="element-1542" data-mp="2v2c11ks7nh8y" />
                    <div className="w-3 h-3 rounded-full bg-[#27C93F]" data-id="element-1543" data-mp="2z4fol7s5mcal" />
                  </div>
                  <div className="flex items-center gap-2 text-[#9C8E82] text-xs font-medium truncate" data-id="element-1544" data-mp="2z69y7pxz6hzd">
                    <Cpu size={12} className="opacity-70 shrink-0 w-3.5 h-3.5" data-id="element-1545" data-mp="2qc5dlo9fq2sa" />
                    <span className="truncate" data-id="element-1546" data-mp="2qa0x86ucnbb4">
                      agent-7f32 · isolated sandbox
                    </span>
                  </div>
                  <div className="w-24 flex justify-end" data-id="element-1547" data-mp="2ge7nswbxfxxk">
                    <div className="flex items-center gap-1.5 px-1.5 py-0.5 rounded border border-[#F97316]/30 bg-[#F97316]/10 text-[#F97316] text-[10px] font-bold tracking-wider uppercase" data-id="element-1548" data-mp="2gbl98cqmci5u">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#F97316] animate-pulse" data-id="element-1549" data-mp="27fdo2x3aurfs" />
                      LIVE
                    </div>
                  </div>
                </div>

                {/* Tabs */}
                <div className="flex items-center px-4 border-b border-white/[0.06] bg-[#241B14] text-xs" data-id="element-1550" data-mp="379dirj04oae">
                  <div className="px-4 py-2 border-b-2 border-[#F97316] text-white font-medium" data-id="element-1551" data-mp="3vbae5z4t9u3b">
                    Terminal
                  </div>
                  <div className="px-4 py-2 text-[#9C8E82] font-medium" data-id="element-1552" data-mp="3qch4yiwk4p8y">
                    Files
                  </div>
                  <div className="px-4 py-2 text-[#9C8E82] font-medium" data-id="element-1553" data-mp="3lf79alikrhco">
                    Browser
                  </div>
                </div>

                {/* Terminal Body */}
                <div className="p-6 min-h-[240px] bg-[#1C1510] text-[#E7E0D7] overflow-hidden" data-id="element-1554" data-mp="hwxfluz0bqz6">
                  <div className="flex flex-col gap-1.5 w-full whitespace-pre-wrap" data-id="element-1555" data-mp="d3ky04sqwkfx">
                    {TERMINAL_LINES.slice(0, visibleLines).map((line, i) => {
                    if (line.type === 'empty') return <div key={i} className="h-4" data-id="element-1556" data-mp="15n1andy4qiek" />;
                    const isLastVisible = i === visibleLines - 1;
                    const isPrompt = line.type === 'prompt';
                    const isCommand = line.type === 'command';
                    const isSuccess = line.type === 'success';
                    if (isPrompt) {
                      const nextLine = TERMINAL_LINES[i + 1];
                      const hasCommand = nextLine && nextLine.type === 'command' && visibleLines > i + 1;
                      const isCommandLastVisible = hasCommand && visibleLines === i + 2;
                      return <div key={i} className="flex items-center gap-2" data-id="element-1557" data-mp="1r2xri3w7spns">
                            <span className="text-[#F97316]" data-id="element-1558" data-mp="1w2irmhn4f423">{line.text}</span>
                            {hasCommand ? <div className="flex items-center gap-0.5" data-id="element-1559" data-mp="20w4uji3hftkp">
                                <span className="text-[#FAF8F4]" data-id="element-1560" data-mp="25u6cu0t33ov4">
                                  <TypewriterText text={nextLine.text} data-id="element-1561" data-mp="2aszsnzvufryg" />
                                </span>
                                {isCommandLastVisible && <span className="w-2 h-4 bg-[#F97316] animate-pulse" data-id="element-1562" data-mp="2ettjb10o7kv1" />}
                              </div> : isLastVisible && <span className="w-2 h-4 bg-[#F97316] animate-pulse" data-id="element-1563" data-mp="3048h37max6si" />}
                          </div>;
                    }
                    if (isCommand) return null;
                    if (isSuccess) return <div key={i} className="text-[#F97316] font-semibold" data-id="element-1564" data-mp="3a2eizp83ycxd">
                            {line.text}
                          </div>;
                    return <div key={i} className="text-[#9C8E82]" data-id="element-1565" data-mp="2hf1x7h9ru954" data-mpi={i}>
                          {line.text}
                        </div>;
                  })}
                  </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between px-4 py-2.5 border-t border-white/[0.06] bg-[#241B14] text-xs text-[#9C8E82]" data-id="element-1566" data-mp="2r550caalx3h6">
                  <div className="flex items-center gap-4" data-id="element-1567" data-mp="2mc22vr2pl9vp">
                    <div className="flex items-center gap-1.5" data-id="element-1568" data-mp="1yniulbjnsx3m">
                      <Cpu size={12} className="w-3.5 h-3.5" data-id="element-1569" data-mp="1tpr0qbzgf0ws" />2 vCPU
                    </div>
                    <div className="flex items-center gap-1.5" data-id="element-1570" data-mp="97f2ii5h7yxy">
                      <HardDrive size={12} className="w-3.5 h-3.5" data-id="element-1571" data-mp="9643swqw1hdp" />
                      14.8 GB free
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 text-[#E7E0D7]" data-id="element-1572" data-mp="7a7z8ldsv17">
                    <Shield size={12} className="w-3.5 h-3.5" data-id="element-1573" data-mp="48c1d8smacv9" />
                    isolated
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* MOBILE LAYOUT (New Version) */}
      <section className="md:hidden relative w-full bg-[#FAF8F4] py-12 overflow-hidden" data-id="element-1574" data-mp="ww852xglt51j">
        {/* Subtle warm dot-grid backdrop, matching the desktop surface. */}
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 z-0 opacity-60" style={{
        backgroundImage: 'radial-gradient(rgba(43,33,26,0.10) 1px, transparent 1px)',
        backgroundSize: '22px 22px'
      }} data-id="element-1575" data-mp="nv9yq8ip0lfs" />
        <div className="relative z-10 max-w-3xl mx-auto px-6 flex flex-col items-start" data-id="element-1576" data-mp="38kh7kx6xmend">
          <motion.div initial={reducedMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.6
        }} className="w-full mb-4" data-id="element-1577" data-mp="1ry1qmxchj3dm">
            <span className="block text-xs font-semibold tracking-[5px] uppercase text-neutral-400 mb-5" data-id="element-1578" data-mp="188zozeklltsa">
              Under the hood
            </span>
            <h2 className="text-3xl md:text-5xl font-title font-medium tracking-tight leading-[1.1] text-[#2B211A]" data-id="element-1579" data-mp="1d69bomglrrcf">
              {copy.computerUse.headline}
            </h2>
          </motion.div>

          <motion.div initial={reducedMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.6,
          delay: reducedMotion ? 0 : 0.2
        }} className="w-full mb-6" data-id="element-1580" data-mp="149s05i4btrg5">
            {/* Composed scene (replaces the video): the isolated-sandbox
             terminal sits to the upper-right, and the grayscale "computer-head
             person with laptop" overlaps it in front from the lower-left —
             matching the reference exactly. Both are static images. */}
            <div className="relative w-full aspect-[3/3.4]" data-id="element-1581" data-mp="3rhy2e4evo9pd">
              {/* Terminal window — top-right, behind the person. */}
              <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Container_(4).png" alt="Giga running tasks in an isolated cloud sandbox terminal" className="absolute top-[2%] right-0 w-[74%] h-auto z-10 drop-shadow-2xl" data-id="element-1582" data-mp="4bxatuwvchhn" />
              {/* Computer-head person with laptop — lower-left, in front.
               Enlarged to match the Figma reference where the figure is the
               dominant element of the composition. */}
              <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/image_32.png" alt="Giga, illustrated as a computer-headed figure working on a laptop" className="absolute bottom-0 -left-[4%] w-[88%] h-auto z-20" data-id="element-1583" data-mp="a0ouxovxfb2e" />
            </div>
          </motion.div>

          <motion.div initial={reducedMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.6,
          delay: reducedMotion ? 0 : 0.4
        }} className="w-full" data-id="element-1584" data-mp="nxki0l90j31g">
            <p className="text-base text-neutral-500 leading-relaxed font-medium" data-id="element-1585" data-mp="17lkwj16lzzop">
              Every task runs on Giga's own secure machine in the cloud — it
              writes code, crunches files, and opens a browser when work lives
              behind a login.
            </p>
          </motion.div>
        </div>
      </section>
    </>;
}